import React, { useState } from 'react';
import moment from 'moment';
import styled from '@emotion/styled';

import { Box, IconButton, SrOnly, Tooltip, useId } from '@applyboard/ui-components';
import { CalendarOutlineIcon } from '@applyboard/ui-icons';
import { raiseSuccessMessage, useEventTracker } from '@applyboard/ui-utils';

import useInitialProps from '~/hooks/useInitialProps';
import CalendarInputCore from '~/components/core/input/CalendarInput';
import handleApiErrorResponse from '~/util/handleApiErrorResponse';
import { patchApplicationRequirementAction } from '~/api/jsonapi';
import useDebouncedState from '~/hooks/useDebouncedState';

import { REJECTED_STATE } from '~/components/style/colors';

type DueDateProps = {
  userPermissions: { [key: string]: boolean };
  requirement: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  dispatch: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  userIsAdmin: boolean;
  completed: boolean;
  reloadAggregatedData?: () => void;
};

const DueDate = ({
  userPermissions,
  requirement,
  dispatch,
  userIsAdmin,
  completed,
  reloadAggregatedData,
}: DueDateProps) => {
  const [calendarInputVisible, setCalendarInputVisible] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [calendarInputStatus, _, setCalendarInputStatus] = useDebouncedState('hidden', {
    delay: 500,
    onChange: () => {
      if (calendarInputVisible && calendarInputStatus === 'hidden') {
        onToggleCalendarInput();
      }
    },
  });
  const { trackEvent } = useEventTracker();
  const { role: userRole } = useInitialProps() as { role: string };
  const tooltipId = useId();
  const onToggleCalendarInput = React.useCallback(() => {
    if (calendarInputVisible && calendarInputStatus !== 'hidden') {
      setCalendarInputStatus('hidden');
    }

    setCalendarInputVisible(!calendarInputVisible);
  }, [calendarInputVisible]);

  const calendarIconVisible =
    (userPermissions.create_requirement_with_template ||
      userPermissions.create_requirement_without_template) &&
    !requirement.deadline &&
    !calendarInputVisible;

  const displayDueDate = !!requirement.deadline && !calendarInputVisible;

  const buttonLabel =
    completed && !userPermissions.modify_application_requirement_deadline
      ? 'The requirement cannot be edited because it has been completed'
      : 'Click to add due date';

  const onDateChange = async (e, date) => {
    try {
      const response = await dispatch(
        patchApplicationRequirementAction(
          { application_requirement_id: requirement.id },
          {
            data: {
              attributes: {
                deadline: date,
              },
            },
          },
        ),
      );
      if (date) {
        raiseSuccessMessage(`Updated due date to ${date}`);
      } else {
        raiseSuccessMessage('Removed due date');
      }
      setCalendarInputVisible(false);
      trackEvent({
        event: `Single Application - Applicant Requirements - ${
          requirement.deadline ? 'Updated Due Date On' : 'Added Due Date To'
        } Requirements`,
        data: {
          userRole,
          requirementType: requirement.requirement_template_type,
          requirementCategory: requirement.requirement_category,
          requirementTemplateId: requirement.requirement_template_id,
          applicationStage: requirement.application_stage,
          requirementInstanceId: requirement.id,
          isEarlyAccess: requirement.is_early_access,
        },
      });
      reloadAggregatedData();
      return response;
    } catch (err) {
      handleApiErrorResponse(err).catch(() => {});
    }

    return null;
  };

  return (
    <>
      {(userPermissions.create_requirement_with_template ||
        userPermissions.create_requirement_without_template) &&
        calendarInputVisible && (
          <Box ml={1}>
            <CalendarInputCore
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              name="requirement_due_date"
              inputId="requirement_due_date"
              onChange={onDateChange}
              onFocus={() => setCalendarInputStatus('focused')}
              onBlur={() => setCalendarInputStatus('hidden')}
              onOpen={() => setCalendarInputStatus('opened')}
              onClose={() => setCalendarInputStatus('hidden')}
              inputValue={requirement.deadline}
              minDate={moment().add(1, 'days')}
              maxDate={moment().add(5, 'years')}
              initialFocusedDate={moment().add(1, 'days')}
            />
          </Box>
        )}
      {calendarIconVisible && userIsAdmin && (
        <Tooltip placement="top" title={<span id={tooltipId}>{buttonLabel}</span>}>
          <IconButton
            size="small"
            ml={1}
            aria-label="Edit due date"
            aria-describedby={tooltipId}
            onClick={
              completed && !userPermissions.modify_application_requirement_deadline
                ? undefined
                : onToggleCalendarInput
            }
          >
            <CalendarOutlineIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}
      {displayDueDate && (
        <DueDateDisplay
          onToggleCalendarInput={
            userPermissions.modify_application_requirement_deadline && userIsAdmin
              ? onToggleCalendarInput
              : null
          }
          canEditDueDate={userPermissions.modify_application_requirement_deadline && userIsAdmin}
          dueDate={requirement.deadline}
        />
      )}
    </>
  );
};

const DueDateDisplay = ({
  dueDate,
  onToggleCalendarInput,
  canEditDueDate,
}: {
  dueDate: string;
  onToggleCalendarInput: () => void;
  canEditDueDate?: boolean;
}) => {
  const label = canEditDueDate ? 'Click to change due date' : null;
  return (
    <Tooltip title={label} placement="top">
      <DueDateDisplayStyled
        canEditDueDate={canEditDueDate}
        onClick={onToggleCalendarInput}
        aria-label={label}
        disabled={!canEditDueDate}
      >
        <CalendarOutlineIcon mr={0.5} color="negative.default" />
        <SrOnly>Due date: </SrOnly>
        <span> {dueDate} </span>
      </DueDateDisplayStyled>
    </Tooltip>
  );
};

const DueDateDisplayStyled = styled.button<{ canEditDueDate?: boolean }>`
  color: ${REJECTED_STATE};
  cursor: ${({ canEditDueDate }) => (canEditDueDate ? 'pointer' : 'default')};
  padding: 0;
  margin-left: 8px;
  font-size: 1.1em;
  position: relative;
  display: flex;
  align-items: center;
  border: none;
  background: none;
`;

export default DueDate;
